import moment from 'moment';
import { hasProctoringCheckBefore } from './proctoringSystemCheckStorage';

const PRE_EXAM_LEAD_MS = 15 * 60 * 1000;

/**
 * Normalize API base (REACT_APP_MY_VAR) for proctoring widget URLs.
 * @param {string} [raw]
 * @returns {string}
 */
export function proctoringBaseUrl(raw) {
  const s = (raw || process.env.REACT_APP_MY_VAR || '').trim();
  if (!s) return '';
  return s.replace(/\/+$/, '');
}

/**
 * @param {object|null|undefined} user — loginReducer.user
 * @param {object|null|undefined} programsFull — userProgramsFull slice
 * @returns {{ startMs: number, endMs: number }}
 */
export function resolveVirgilExamWindow(user, programsFull) {
  const virgilExamId =
    user && user.virgil_examId != null ? String(user.virgil_examId) : null;
  const libId =
    programsFull && programsFull.examTimeLibraryId != null
      ? String(programsFull.examTimeLibraryId)
      : null;

  let trustReduxTimes = false;
  if (programsFull && programsFull.examTimeStart && programsFull.examTimeEnd) {
    if (libId == null || libId === '') {
      trustReduxTimes = true;
    } else if (virgilExamId && libId === virgilExamId) {
      trustReduxTimes = true;
    }
  }

  let startMs = NaN;
  let endMs = NaN;

  if (trustReduxTimes) {
    startMs = Date.parse(String(programsFull.examTimeStart));
    endMs = Date.parse(String(programsFull.examTimeEnd));
  }

  if (!Number.isFinite(endMs) || endMs <= 0) {
    endMs = user && user.virgil_due ? Date.parse(String(user.virgil_due)) : NaN;
  }

  if (!Number.isFinite(startMs) || startMs <= 0) {
    if (user && user.virgil_start != null) {
      startMs = Date.parse(String(user.virgil_start));
    }
  }

  return { startMs, endMs };
}

/**
 * @param {number} startMs
 * @param {number} endMs
 * @param {number} [now]
 */
export function isExamActiveInWindow(startMs, endMs, now = Date.now()) {
  if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
    return now >= startMs && now < endMs;
  }
  if (Number.isFinite(endMs)) {
    return now < endMs;
  }
  if (Number.isFinite(startMs)) {
    return now >= startMs;
  }
  // No known window: preserve legacy behaviour for Virgil until API supplies times
  return true;
}

/**
 * Same local calendar day as "now" (student device).
 * @param {number} dateMs
 * @param {number} [now]
 */
export function isLocalCalendarExamDay(dateMs, now = Date.now()) {
  if (!Number.isFinite(dateMs)) return false;
  return moment(dateMs).isSame(moment(now), 'day');
}

/**
 * Value for ProctoringWidget.examStartAt — only on exam local day.
 * @param {number} startMs
 * @param {number} [now]
 */
export function examStartAtForWidget(startMs, now = Date.now()) {
  if (!Number.isFinite(startMs)) return undefined;
  if (!isLocalCalendarExamDay(startMs, now)) return undefined;
  return startMs;
}

export { PRE_EXAM_LEAD_MS };

/**
 * Whether to show "complete system check before exam" warning in exam UI.
 * @param {object} user
 * @param {object} programsFull
 * @param {string|number|null|undefined} contentSurveyId — selected content survey_id
 * @param {number} [now]
 */
export function getProctoringExamDayWarningState(
  user,
  programsFull,
  contentSurveyId,
  now = Date.now()
) {
  if (!user || user.is_virgil != 1) return { show: false };
  if (
    user.virgil_examId != null &&
    contentSurveyId != null &&
    String(user.virgil_examId) !== String(contentSurveyId)
  ) {
    return { show: false };
  }

  const { startMs } = resolveVirgilExamWindow(user, programsFull);
  if (!Number.isFinite(startMs) || now >= startMs) return { show: false };
  if (!isLocalCalendarExamDay(startMs, now)) return { show: false };

  const deadline = startMs - PRE_EXAM_LEAD_MS;
  if (now < deadline) return { show: false };
  if (hasProctoringCheckBefore(user.userId, user.virgil_examId, deadline))
    return { show: false };

  return {
    show: true,
    message:
      'Proctoring system check should be completed at least 15 minutes before the exam starts. Complete the system check in the proctoring panel, then continue.',
  };
}
